// pages/vault/helpers/expandVaultEntries.ts

export type RestrictType = 'all_media' | 'photos' | 'videos'

/**
 * Expand a single vault "entry" into one item per media object (photo/video),
 * so grouped=false can render one card per thumb in a virtualized grid.
 *
 * Returns items with a stable-ish __k key you can use for React keys.
 */
export function expandVaultEntry(media: any, restrictType: RestrictType = 'all_media'): any[] {
  const rt: RestrictType = restrictType ?? 'all_media'

  const base =
    media?.id ??
    media?.post?.id ??
    media?.message?.message?.id ??
    media?.story?.id ??
    Math.random()

  const baseKey = String(base)
  const out: any[] = []

  // ---------------- POSTS ----------------
  if (media?.post) {
    // photos
    if (rt !== 'videos') {
      const photos = Array.isArray(media?.post?.photos) ? media.post.photos : []
      for (const p of photos) {
        const pid = p?.id ?? Math.random()
        out.push({
          ...media,
          __k: `${baseKey}-post-photo-${pid}`,
          // narrow to single media item
          post: {
            ...(media.post || {}),
            photos: [p],
            videos: [],
          },
        })
      }
    }

    // videos
    if (rt !== 'photos') {
      const videos = Array.isArray(media?.post?.videos) ? media.post.videos : []
      for (const v of videos) {
        const vid = v?.id ?? Math.random()
        out.push({
          ...media,
          __k: `${baseKey}-post-video-${vid}`,
          post: {
            ...(media.post || {}),
            photos: [],
            videos: [v],
          },
        })
      }
    }
  }

  // ---------------- MESSAGES ----------------
  if (media?.message?.message) {
    const msg = media.message.message

    // photos
    if (rt !== 'videos') {
      const photos = Array.isArray(msg?.photos) ? msg.photos : []
      for (const p of photos) {
        const pid = p?.id ?? Math.random()
        out.push({
          ...media,
          __k: `${baseKey}-msg-photo-${pid}`,
          message: {
            ...(media.message || {}),
            message: {
              ...(msg || {}),
              photos: [p],
              videos: [],
            },
          },
        })
      }
    }

    // videos
    if (rt !== 'photos') {
      const videos = Array.isArray(msg?.videos) ? msg.videos : []
      for (const v of videos) {
        const vid = v?.id ?? Math.random()
        out.push({
          ...media,
          __k: `${baseKey}-msg-video-${vid}`,
          message: {
            ...(media.message || {}),
            message: {
              ...(msg || {}),
              photos: [],
              videos: [v],
            },
          },
        })
      }
    }
  }

  // ---------------- STORY ----------------
  if (media?.story) {
    const hasPhoto = !!media.story.image_src
    const hasVideo = !!media.story.video_src

    if (rt === 'all_media' && (hasPhoto || hasVideo)) out.push({ ...media, __k: `${baseKey}-story` })
    else if (rt === 'photos' && hasPhoto) out.push({ ...media, __k: `${baseKey}-story-photo` })
    else if (rt === 'videos' && hasVideo) out.push({ ...media, __k: `${baseKey}-story-video` })
  }

  // If nothing expanded, keep original entry so UI doesn't break.
  return out.length ? out : [{ ...media, __k: `${baseKey}-fallback` }]
}

export function expandVaultEntries(list: any[] | undefined | null, restrictType: RestrictType = 'all_media'): any[] {
  const arr = Array.isArray(list) ? list : []
  return arr.flatMap(item => expandVaultEntry(item, restrictType))
}